import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  useAtividadesCarroQuery,
  useBulkAtividadesCarroMutation,
} from '../../../infrastructure/react-query/hooks/useAtividadesCarroQueries';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { AtividadeCarro, AtividadeEstado, isServicoEmAberto } from '../../../interfaces/AtividadeCarro';
import { getCar } from '../../../api/carsApi';
import {
  SubtarefaDefinicao,
  listarSubtarefas,
  obterProgressoDoVeiculo,
} from '../../../api/subtarefasApi';
import { notify } from '../../../shared/feedback/notify';

import './CarServicesPlanningModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface CarServicesPlanningModalProps {
  isOpen: boolean;
  carId: string | null;
  carPlate?: string;
  carModel?: string;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

const CarServicesPlanningModal: React.FC<CarServicesPlanningModalProps> = ({
  isOpen,
  carId,
  carPlate,
  carModel,
  onCancel,
  sidebarRef,
}) => {
  const { esteirasPlanejaveis } = useEsteiras();
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  // at-46: o estado do serviço por esteira substitui a inferência "realizada = período
  // APPROVED no histórico" — que dava esteira concluída para serviço apenas pausado.
  const [estadoPorEsteira, setEstadoPorEsteira] = useState<Record<string, AtividadeEstado>>({});

  /**
   * at-89: catálogo de sub-tarefas por esteira, a seleção atual do veículo e o que já foi
   * concluído. `catalogoCarregou` distingue "esta esteira não tem sub-tarefas" de "não consegui
   * carregar o catálogo" — sem isso, uma falha de rede faria o Chefe salvar o plano sem as
   * sub-tarefas e o colaborador ficaria sem nada para marcar.
   */
  const [catalogo, setCatalogo] = useState<Record<string, SubtarefaDefinicao[]>>({});
  const [catalogoCarregou, setCatalogoCarregou] = useState(false);
  const [subtarefasSelecionadas, setSubtarefasSelecionadas] = useState<Record<string, string[]>>({});
  const [subtarefasConcluidas, setSubtarefasConcluidas] = useState<Record<string, string[]>>({});

  // Só esteiras marcadas como planejáveis no cadastro (exclui as de sistema,
  // como Orçamento — padrões não entram no planejamento de serviços).
  const plannableLanes = esteirasPlanejaveis;

  useEffect(() => {
    if (!isOpen || !carId) {
      setCurrentStatus(null);
      return;
    }

    getCar(carId)
      .then((data) => {
        setCurrentStatus(data?.status || null);
      })
      .catch((err) => {
        // Sem a esteira atual, o planejamento é exibido sem a referência de onde
        // o veículo está — o erro precisa aparecer.
        notify.errorFrom(
          err,
          'Não foi possível carregar a esteira atual do veículo. Recarregue a página; se persistir, acione a TI.',
        );
      });
  }, [isOpen, carId]);

  /**
   * at-89: uma carga por abertura — o catálogo inteiro (não por esteira) e a seleção já salva.
   * Buscar por esteira geraria N requisições a cada clique no planejamento.
   */
  useEffect(() => {
    if (!isOpen || !carId) {
      setCatalogo({});
      setCatalogoCarregou(false);
      setSubtarefasSelecionadas({});
      setSubtarefasConcluidas({});
      return;
    }

    let cancelado = false;

    void (async () => {
      try {
        const [definicoes, progresso] = await Promise.all([
          listarSubtarefas(),
          obterProgressoDoVeiculo(carId),
        ]);
        if (cancelado) return;

        const porEsteira: Record<string, SubtarefaDefinicao[]> = {};
        for (const def of definicoes) {
          if (!porEsteira[def.esteiraId]) porEsteira[def.esteiraId] = [];
          porEsteira[def.esteiraId].push(def);
        }
        setCatalogo(porEsteira);

        const selecionadas: Record<string, string[]> = {};
        const concluidas: Record<string, string[]> = {};
        for (const esteira of progresso.esteiras) {
          selecionadas[esteira.esteiraId] = esteira.subtarefas.map((s) => s.definicaoId);
          concluidas[esteira.esteiraId] = esteira.subtarefas
            .filter((s) => s.concluida)
            .map((s) => s.definicaoId);
        }
        setSubtarefasSelecionadas(selecionadas);
        setSubtarefasConcluidas(concluidas);
        setCatalogoCarregou(true);
      } catch (error) {
        if (cancelado) return;
        setCatalogoCarregou(false);
        notify.errorFrom(
          error,
          'Não foi possível carregar as sub-tarefas das esteiras. Salvar agora manteria a seleção atual — recarregue a página antes de alterá-la.',
        );
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [isOpen, carId]);

  const bulkPlanningMutation = useBulkAtividadesCarroMutation();
  const saving = bulkPlanningMutation.isPending;

  const activitiesQuery = useAtividadesCarroQuery(
    { carId: carId || '', isActive: true },
    Boolean(isOpen && carId),
  );

  const loading = activitiesQuery.isLoading;

  useEffect(() => {
    if (!isOpen || !carId) {
      setSelectedStatuses([]);
      return;
    }

    const items: AtividadeCarro[] = activitiesQuery.data || [];
    const sorted = items.slice().sort((a, b) => {
      const ao = a.order ?? Number.POSITIVE_INFINITY;
      const bo = b.order ?? Number.POSITIVE_INFINITY;

      if (ao !== bo) return ao - bo;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    setSelectedStatuses(sorted.map((x) => x.esteiraId || x.status || '').filter(Boolean));

    // at-46: mapa esteira → estado do serviço, base dos badges e do bloqueio de desmarcar.
    const estados: Record<string, AtividadeEstado> = {};
    for (const item of items) {
      const key = item.esteiraId || item.status;
      if (key && item.estado) estados[key] = item.estado;
    }
    setEstadoPorEsteira(estados);
  }, [isOpen, carId, activitiesQuery.data]);

  useEffect(() => {
    if (activitiesQuery.error) {
      toast.error(
        (activitiesQuery.error as { message?: string } | null)?.message ||
          'Erro ao carregar planejamento de serviços',
      );
    }
  }, [activitiesQuery.error]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef]);

  if (!isOpen) return null;

  const toggleStatus = (status: string) => {
    // at-46: serviço em aberto (em andamento ou inacabado) não sai do planejamento — a
    // pendência desapareceria das telas sem ninguém decidir o desfecho. O backend também
    // recusa (409 SERVICO_EM_ABERTO); aqui o aviso é imediato.
    const estado = estadoPorEsteira[status];
    if (isServicoEmAberto(estado) && selectedStatuses.includes(status)) {
      toast.error(
        estado === 'PAUSADA'
          ? 'Este serviço ficou inacabado e segue como pendência. Conclua-o pelo veículo (ou aguarde a retomada) antes de removê-lo do planejamento.'
          : 'Há atividade em andamento nesta esteira. Aguarde a finalização antes de removê-la do planejamento.',
      );
      return;
    }

    setSelectedStatuses((prev) => {
      const has = prev.includes(status);
      if (has) {
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  };

  /**
   * at-89: liga/desliga uma sub-tarefa da esteira. Já CONCLUÍDA não sai — ela é fato, não plano
   * (o backend preserva de qualquer forma; aqui o aviso é imediato).
   */
  const toggleSubtarefa = (esteiraId: string, definicaoId: string) => {
    if ((subtarefasConcluidas[esteiraId] ?? []).includes(definicaoId)) return;

    setSubtarefasSelecionadas((prev) => {
      const atuais = prev[esteiraId] ?? [];
      const tem = atuais.includes(definicaoId);
      return {
        ...prev,
        [esteiraId]: tem ? atuais.filter((id) => id !== definicaoId) : [...atuais, definicaoId],
      };
    });
  };

  const handleSavePlanning = async () => {
    if (!carId) return;

    /**
     * at-68 (BUG-V50): envia na ORDEM EM QUE O CHEFE SELECIONOU.
     *
     * O código antigo remapeava por `plannableLanes` — a ordem do **cadastro de esteiras** —
     * e só filtrava pela seleção, descartando a sequência escolhida. O backend grava
     * `order: index` do array recebido (`bulkSetPlanning`), então a ordem do planejamento
     * era sempre a do cadastro, não a do plano. O filtro contra `plannableLanes` continua:
     * ele descarta esteira que deixou de ser planejável.
     */
    const idsPlanejaveis = new Set(plannableLanes.map((est) => est.id));
    const statusesToSend = selectedStatuses.filter((status) => idsPlanejaveis.has(status));

    /**
     * at-89: só declara sub-tarefas quando o catálogo carregou de verdade. Com falha na carga,
     * o campo é OMITIDO e o backend preserva a seleção existente — enviar o estado local vazio
     * apagaria em silêncio o que o Chefe tinha escolhido antes.
     */
    const subtarefasToSend = catalogoCarregou
      ? statusesToSend
          .filter((esteiraId) => (catalogo[esteiraId] ?? []).length > 0)
          .map((esteiraId) => ({
            esteiraId,
            definicaoIds: subtarefasSelecionadas[esteiraId] ?? [],
          }))
      : undefined;

    try {
      const res: AtividadeCarro[] = await bulkPlanningMutation.mutateAsync({
        carId,
        statuses: statusesToSend,
        subtarefas: subtarefasToSend,
      });

      const sorted = (res || []).slice().sort((a, b) => {
        const ao = a.order ?? Number.POSITIVE_INFINITY;
        const bo = b.order ?? Number.POSITIVE_INFINITY;

        if (ao !== bo) return ao - bo;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });

      setSelectedStatuses(sorted.map((x) => x.esteiraId || x.status || '').filter(Boolean));
      toast.success('Planejamento de serviços salvo com sucesso.');
      onCancel();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao salvar planejamento');
    }
  };

  return (
    <div className="add-car-modal-overlay">
      <div className="car-services-modal-content" ref={modalRef}>
        
        {/* Modal Header */}
        <div className="car-services-modal-header">
          <div className="header-info">
            <h2>Serviços que o veículo vai realizar</h2>
            {carPlate && (
              <span className="vehicle-subtitle">
                {carPlate} {carModel ? `- ${carModel}` : ''}
              </span>
            )}
          </div>
          <button className="close-button" onClick={onCancel} aria-label="Fechar modal">
            &times;
          </button>
        </div>
        {loading ? (
          <div className="car-services-loading">
            <div className="spinner"></div>
            <p>Carregando planejamento...</p>
          </div>
        ) : (
          <div className="car-services-modal-body">
            
            <div className="car-services-description-row">
              <p className="description-text">
                Marque abaixo todas as esteiras e serviços que este veículo deverá percorrer durante o processo:
              </p>
              {/* Pedido do usuário (29/07/2026; entregue na at-46): marcar todas as etapas
                  planejáveis de uma vez. Desmarcar continua sendo etapa a etapa. */}
              <button
                type="button"
                className="car-services-select-all"
                onClick={() =>
                  setSelectedStatuses((prev) =>
                    Array.from(new Set([...prev, ...plannableLanes.map((est) => est.id)])),
                  )
                }
                disabled={
                  saving ||
                  plannableLanes.every(
                    (est) => selectedStatuses.includes(est.id) || selectedStatuses.includes(est.chave),
                  )
                }
              >
                Selecionar todas
              </button>
            </div>

            <div className="services-grid">
              {plannableLanes.map((est) => {
                const checked = selectedStatuses.includes(est.id) || selectedStatuses.includes(est.chave);
                const isCurrentStatus = est.id === currentStatus || est.chave === currentStatus;
                // at-46: o estado do serviço é a fonte de verdade (antes: período APPROVED).
                const estado = estadoPorEsteira[est.id] ?? estadoPorEsteira[est.chave];
                const servicoEmAberto = isServicoEmAberto(estado);

                let badgeText = '';
                let badgeClass = '';
                let leftBorderColor = '#e9ecef';

                if (estado === 'PAUSADA') {
                  // Inacabado tem precedência: é a pendência que o Chefe precisa ver, mesmo
                  // quando o veículo está na esteira agora. Roxo = cor exclusiva do estado.
                  badgeText = 'Inacabado';
                  badgeClass = 'badge-purple';
                  leftBorderColor = '#8b5cf6';
                } else if (isCurrentStatus) {
                  badgeText = 'Atual';
                  badgeClass = 'badge-orange';
                  leftBorderColor = '#ea5d0e';
                } else if (estado === 'CONCLUIDA') {
                  badgeText = 'Realizada';
                  badgeClass = 'badge-green';
                  leftBorderColor = '#10b981';
                } else if (estado === 'EM_ANDAMENTO') {
                  badgeText = 'Em andamento';
                  badgeClass = 'badge-orange';
                  leftBorderColor = '#ea5d0e';
                } else if (checked) {
                  badgeText = 'Prevista';
                  badgeClass = 'badge-red';
                  leftBorderColor = '#ef4444';
                }

                // at-89: sub-tarefas desta esteira, exibidas só quando ela está marcada.
                const subtarefasDaEsteira = catalogo[est.id] ?? [];
                const selecionadasAqui = subtarefasSelecionadas[est.id] ?? [];
                const concluidasAqui = subtarefasConcluidas[est.id] ?? [];

                return (
                  <div key={est.id} className="service-item-wrapper">
                    <label
                      className={`service-item-label ${checked ? 'selected' : ''}`}
                      style={{ borderLeft: `4px solid ${leftBorderColor}` }}
                      title={
                        servicoEmAberto
                          ? 'Serviço em aberto nesta esteira — conclua ou aguarde a retomada antes de remover do planejamento'
                          : undefined
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStatus(est.id)}
                        disabled={saving}
                      />
                      <div className="custom-checkbox">
                        {checked && <span className="car-services-checkmark"></span>}
                      </div>
                      <span className="service-text" style={{ flex: 1 }}>
                        {est.nome}
                      </span>
                      {badgeText && (
                        <span className={`next-sector-status-badge ${badgeClass}`} style={{ marginLeft: '8px' }}>
                          {badgeText}
                        </span>
                      )}
                    </label>

                    {checked && subtarefasDaEsteira.length > 0 && (
                      <div className="subtarefas-do-servico">
                        <span className="subtarefas-do-servico-titulo">
                          O que será feito nesta etapa
                        </span>
                        {subtarefasDaEsteira.map((sub) => {
                          const marcada = selecionadasAqui.includes(sub.id);
                          const jaConcluida = concluidasAqui.includes(sub.id);

                          return (
                            <label
                              key={sub.id}
                              className={`subtarefa-item ${jaConcluida ? 'concluida' : ''}`}
                              title={
                                jaConcluida
                                  ? 'Já foi concluída neste veículo — permanece no histórico mesmo se a etapa sair do plano'
                                  : sub.nome
                              }
                            >
                              <input
                                type="checkbox"
                                checked={marcada || jaConcluida}
                                onChange={() => toggleSubtarefa(est.id, sub.id)}
                                disabled={saving || jaConcluida}
                              />
                              <span className="subtarefa-nome">{sub.nome}</span>
                              {jaConcluida && (
                                <span className="subtarefa-badge-concluida">Já concluída</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="car-services-actions">
              <button
                className="cancel-button"
                onClick={onCancel}
                disabled={saving}
                type="button"
              >
                Cancelar
              </button>
              <button
                className="save-button"
                onClick={handleSavePlanning}
                disabled={saving}
                type="button"
              >
                {saving ? 'Salvando...' : 'Salvar Planejamento'}
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default CarServicesPlanningModal;
